/* HelloCues overhaul — behaviour on top of Concept.
   Kept in its own file so the work stays separable from the theme's own JS and
   survives a Concept update. Loaded deferred, after theme.js.

   Everything here registers *autonomous* custom elements. Concept itself leans
   heavily on customized built-ins (is="hover-link" and friends), which Safari
   has never implemented — fine for the hover decoration Concept uses them for,
   but not for anything a shopper needs in order to buy. */

(() => {
  'use strict';

  /* ------------------------------------------------------------------ scents
     The Beard Oil is three separate products, one per scent, rather than one
     product with three variants — so this swaps whole products, and cannot use
     the theme's variant picker.

     Updates every [data-scent-link] in the step (the media link, the title link
     and the call to action) plus the image, without a page reload. */
  class RoutineScentPicker extends HTMLElement {
    connectedCallback() {
      this.step = this.closest('[data-scent-step]');
      if (!this.step) return;

      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches('.routine-step__scent-input')) return;
        this.select(input);
      });
    }

    select(input) {
      const url = input.value;
      if (url) {
        this.step.querySelectorAll('[data-scent-link]').forEach((link) => {
          link.href = url;
        });
      }

      const img = this.step.querySelector('[data-scent-image]');
      if (!img) return;

      const src = input.dataset.image;
      const srcset = input.dataset.srcset;
      const alt = input.dataset.alt;

      // Setting srcset before src avoids a flash of the old bitmap: the browser
      // re-runs selection from the candidate list rather than painting src first.
      if (srcset) img.srcset = srcset;
      if (src) img.src = src;
      if (alt) img.alt = alt;
    }
  }

  if (!customElements.get('routine-scent-picker')) {
    customElements.define('routine-scent-picker', RoutineScentPicker);
  }

  /* -------------------------------------------------- variant-aware panels
     Concept re-fetches the whole product section when the variant changes, but
     only swaps a fixed list of ids into the page — Price, StickyPrice, Sku,
     Inventory, Volume, PricePerItem, BackInStock and the quantity rules. Any
     other block keeps the first variant's markup.

     That is why the Beard Care Set showed Signature's copy on every scent: the
     PDP panels are metafield-driven, and metafields on the *product* cannot
     vary by variant at all. The Set's scent notes now live on each variant, and
     this swaps the panels marked [data-variant-content] out of the HTML Concept
     has already fetched — so there is no second network request and no risk of
     the two responses disagreeing.

     Limitation worth knowing: this swaps a panel's contents, not its existence.
     A panel that renders for one variant and not another would go stale rather
     than disappear. Every Set variant carries notes, so that case does not
     arise today; if it ever does, the whole <details> needs replacing instead. */
  /* ------------------------------------------------------- complete routine
     "Shop the complete routine" was a plain link, and to three different
     destinations across the three templates that carry this module. The client
     asked for one action that adds the selected Beard Oil, the cream and the
     kit together.

     The variants come from the steps themselves rather than a separate product
     list, so the button always matches what is displayed above it — including
     the scent the shopper has actually picked. Adding a step to the module in
     the theme editor adds it to the routine automatically.

     Uses the same /cart/add.js items[] call and the same cartUpdate publish +
     drawer show that Concept's own ProductBundleForm uses, so the cart drawer
     refreshes exactly as it does everywhere else. */
  class RoutineBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-routine-bundle-submit]');
      this.errorEl = this.querySelector('[data-routine-bundle-error]');
      this.section = this.closest('.shopify-section, [id^="shopify-section"]') || document;
      this.button?.addEventListener('click', () => this.submit());
    }

    get steps() {
      return Array.from(this.section.querySelectorAll('.routine-step[data-step-variant]'));
    }

    // A step showing scents resolves to whichever scent is currently selected;
    // any other step resolves to its own variant.
    itemsForCart() {
      const items = [];
      for (const step of this.steps) {
        const checked = step.querySelector('.routine-step__scent-input:checked');
        const id = checked?.dataset.variantId || step.dataset.stepVariant;
        if (!id) continue;
        const existing = items.find((i) => i.id === id);
        if (existing) existing.quantity += 1;
        else items.push({ id, quantity: 1 });
      }
      return items;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.toggleAttribute('hidden', !message);
    }

    async submit() {
      const items = this.itemsForCart();
      this.showError('');

      if (!items.length) {
        this.showError('Nothing to add — no products are set on this routine.');
        return;
      }

      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');

      try {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
        );

        const response = await fetch(window.theme.routes.cart_add_url, {
          ...window.theme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const parsed = await response.json();

        // cart/add.js reports a failure in the body, not the HTTP status.
        if (parsed.status) {
          this.showError(parsed.description || parsed.message || 'Sorry, that could not be added.');
          return;
        }

        const cart = await (await fetch(window.theme.routes.cart_url, { ...window.theme.utils.fetchConfig() })).json();
        cart.sections = parsed.sections;

        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'routine-bundle',
          cart,
        });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));

        document.querySelector('cart-drawer')?.show(this.button);
      } catch (error) {
        this.showError('Sorry, something went wrong. Please try again.');
        console.error(error);
      } finally {
        this.button.removeAttribute('aria-busy');
        this.button.removeAttribute('aria-disabled');
      }
    }
  }

  if (!customElements.get('routine-bundle')) {
    customElements.define('routine-bundle', RoutineBundle);
  }

  /* ------------------------------------------------------ Method attribution
     The playbook requires campaign attribution to survive the handoff to the
     Method site, which is a different domain. The section already attaches
     utm_source/medium/campaign so the visit is credited to the storefront, but
     that alone would relabel *paid* traffic as coming from us: someone who
     arrived from an Instagram ad and then tapped through to Method would lose
     the original source at the boundary.

     So anything already on the current URL wins. Only parameters the page does
     not already carry are left as the section set them. Runs on click rather
     than at load so it cannot be cached into a static page. */
  const forwardCampaignParams = (link) => {
    try {
      const incoming = new URLSearchParams(window.location.search);
      const carried = [...incoming.keys()].filter(
        (k) => k.startsWith('utm_') || k === 'gclid' || k === 'fbclid' || k === 'ttclid'
      );
      if (!carried.length) return;

      const url = new URL(link.href, window.location.origin);
      carried.forEach((k) => url.searchParams.set(k, incoming.get(k)));
      link.href = url.toString();
    } catch {
      /* A malformed href should never stop the link working. */
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('[data-method-link]');
      if (link) forwardCampaignParams(link);
    },
    true
  );

  const events = window.theme?.pubsub?.PUB_SUB_EVENTS;
  if (events?.variantChange && typeof window.theme.pubsub.subscribe === 'function') {
    window.theme.pubsub.subscribe(events.variantChange, ({ data }) => {
      const parsed = data?.parsedHTML;
      if (!parsed) return;

      document.querySelectorAll('[data-variant-content]').forEach((destination) => {
        const key = destination.getAttribute('data-variant-content');
        // Attribute selector with a quoted value — block ids are Shopify-generated
        // and can contain characters a bare selector would choke on.
        const source = parsed.querySelector(`[data-variant-content="${key}"]`);
        if (source && source.innerHTML !== destination.innerHTML) {
          destination.innerHTML = source.innerHTML;
        }
      });
    });
  }
})();
